import { useContext, useEffect, useRef, useState } from 'react';
import AsyncSelect from 'react-select/async';
import { axiosClient } from '../../../library/Service/axiosClient';
import { GlobalContext } from '../../../library/Core/GlobalContext';
import SessionContext from '../../../library/Core/SessionContext';
import { SmartContext } from '../../../library/Core/SmartContext';
import FactLoader from '../../../library/SimpleControls/FactLoader';
import DebugBarControl from '../../../library/SimpleControls/DebugBarControl';
import { useImmerReducer } from 'use-immer';
import { DispatchEvent, State } from '../../../library/Core/SmartTypes';
import smartReducer from '../../../library/Core/SmartReducer';
import { getDomainElementsForGivenYearRange } from '../../../library/Service/domainService';

import AddNewFormModal from './AddNewFormModal';
import BulkUploadModal from './BulkUploadModal';
import AddMoengageModal from './AddMoengageModal';
import { allowPerformAction } from '../../../library/Core/SmartFunctions';
import { ACTIVITY_CATEGORIES, getActivityCategoryLabel } from './activityCategories';
import { useLocation, useSearchParams } from 'react-router-dom';

const PAGE_SIZE = 25;
const ActivityCsvUploadModal = BulkUploadModal as React.ComponentType<any>;

const ActivityLogReport = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const [autoOpenUpload] = useState(() => {
        const fromUrl = searchParams.get('open_upload') === '1';
        const fromNav = Boolean((location.state as { openUpload?: boolean } | null)?.openUpload);
        let fromStore = false;
        try { fromStore = sessionStorage.getItem('activityLogAutoOpen') === '1'; } catch (_) { /* ignore */ }
        return fromUrl || fromNav || fromStore;
    });
    const initialCategory = (() => {
        const c = searchParams.get('category');
        return c && ACTIVITY_CATEGORIES.some((x) => x.code === c) ? c : '';
    })();

    const [flexText, setFlexText] = useState('');
    const [debouncedFlexText, setDebouncedFlexText] = useState('');
    const [selectedMode, setSelectedMode] = useState('');
    const [selectedCategory, setSelectedCategory] = useState(initialCategory);
    const initialWatchlistId = (() => {
        const id = searchParams.get('watchlist_id');
        return initialCategory === 'WATCHLIST' && id && /^\d+$/.test(id) ? id : '';
    })();
    const [selectedWatchlistId, setSelectedWatchlistId] = useState(initialWatchlistId);
    const [watchlists, setWatchlists] = useState<{ id: number; name: string }[]>([]);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [programs, setPrograms] = useState<any[]>([]);
    const [program, setProgram] = useState('');
    const [batches, setBatches] = useState<any[]>([]);
    const [batch, setBatch] = useState('');
    const [activityLogs, setActivityLogs] = useState<any[]>([]);
    const [activityCount, setActivityCount] = useState<number | undefined>();
    const [refreshKey, setRefreshKey] = useState(0);

    // Tabs
    const [activeTab, setActiveTab] = useState<'activity' | 'moengage'>('activity');
    const [moengageList, setMoengageList] = useState<any[]>([]);
    const [isMoengageLoading, setIsMoengageLoading] = useState(false);

    // Lazy loading state
    const [isLoading, setIsLoading] = useState(true);
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const pageRef = useRef(1);
    const sentinelRef = useRef<HTMLDivElement>(null);
    // Tags each fetch so a slow/late response from a superseded filter set is ignored.
    const requestIdRef = useRef(0);

    // Keep latest filter values accessible inside the observer callback without stale closure
    const loadMoreFnRef = useRef<() => void>(() => {});

    const { globalState } = useContext(GlobalContext);
    const { sessionState } = useContext(SessionContext);
    const isSuperAdmin = sessionState?.userTypeCode === 'SUPER_ADMIN';
    const instituteDomain: any[] = globalState?.domain?.get('INSTITUTE_DOMAIN') || [];
    const instituteOptions = instituteDomain.map((item: any) => ({ value: String(item.code), label: item.value }));
    const campusFilterId = searchParams.get('filter_institute_id') || '';
    const [selectedInstituteId, setSelectedInstituteId] = useState(() => searchParams.get('institute_id') || '');
    const [selectedInstituteOption, setSelectedInstituteOption] = useState<{ value: string; label: string } | null>(() => {
        const instId = searchParams.get('institute_id');
        if (!instId) return null;
        const match = instituteDomain.find((item: any) => String(item.code) === String(instId));
        return match
            ? { value: String(match.code), label: match.value }
            : { value: String(instId), label: String(instId) };
    });

    const [state, dispatch] = useImmerReducer<State, DispatchEvent>(smartReducer, {
        flags: { isDataLoading: true, showFormErrors: 0 },
        formValidationErrors: {},
        actions: {},
    } as any);

    useEffect(() => {
        const category = searchParams.get('category');
        if (category && ACTIVITY_CATEGORIES.some((c) => c.code === category)) {
            setSelectedCategory(category);
        }
        const instId = searchParams.get('institute_id') || selectedInstituteId;
        if (isSuperAdmin && instId) {
            const match = instituteDomain.find((item: any) => String(item.code) === String(instId));
            setSelectedInstituteOption(
                match
                    ? { value: String(match.code), label: match.value }
                    : { value: String(instId), label: String(instId) }
            );
        }
        // Do not strip open_upload here — BulkUploadModal clears it after the dialog is visible.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const clearOpenUploadParam = () => {
        if (searchParams.get('open_upload') !== '1') return;
        const next = new URLSearchParams(searchParams);
        next.delete('open_upload');
        setSearchParams(next, { replace: true });
    };

    // Debounce flexText — only trigger search after 500 ms of inactivity
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedFlexText(flexText), 500);
        return () => clearTimeout(timer);
    }, [flexText]);

    const buildPayload = (pageNum: number) => {
        const payload: any = {
            startDate,
            endDate,
            mode: selectedMode,
            category: selectedCategory,
            flexText: debouncedFlexText.length > 2 ? debouncedFlexText : null,
            program,
            batch,
            page: pageNum,
        };
        if (isSuperAdmin && selectedInstituteId) payload.instituteId = selectedInstituteId;
        if (selectedCategory === 'WATCHLIST' && selectedWatchlistId) payload.watchlistId = selectedWatchlistId;
        return payload;
    };

    const fetchPage = async (pageNum: number, reset: boolean) => {
        if (reset) setIsLoading(true);
        else setIsFetchingMore(true);

        const payload = buildPayload(pageNum);
        const requestId = ++requestIdRef.current;

        // The count query scans the whole (unpaged) result set and is far slower than the
        // LIMIT 50 list. Fire it independently so it never blocks the table render — the
        // header shows "Showing N" immediately and upgrades to "Showing N of M" once it lands.
        if (reset) {
            setActivityCount(undefined);
            axiosClient()
                .post(`${process.env.REACT_APP_INTERNAL_ADMIN_SERVER}/search/activity-making-count`, payload)
                .then((countRes) => {
                    if (requestId === requestIdRef.current && countRes?.status === 200) {
                        setActivityCount(countRes.data[0]?.count ?? 0);
                    }
                })
                .catch(() => { if (requestId === requestIdRef.current) setActivityCount(0); });
        }

        try {
            const listRes = await axiosClient().post(
                `${process.env.REACT_APP_INTERNAL_ADMIN_SERVER}/search/activity-making-list`,
                payload
            );

            // A newer fetch superseded this one — drop the stale response.
            if (requestId !== requestIdRef.current) return;

            if (listRes.status === 200) {
                const newLogs: any[] = listRes.data || [];
                setActivityLogs(prev => (reset ? newLogs : [...prev, ...newLogs]));
                const more = newLogs.length === PAGE_SIZE;
                setHasMore(more);
                pageRef.current = pageNum;
            }
        } catch (err) {
            console.error('Error fetching activity list:', err);
            if (requestId === requestIdRef.current && reset) { setActivityLogs([]); setActivityCount(0); }
        } finally {
            setIsLoading(false);
            if (requestId === requestIdRef.current) {
                setIsFetchingMore(false);
            }
        }
    };

    // Refresh the load-more function every render so the observer always has fresh state
    loadMoreFnRef.current = () => {
        if (!hasMore || isFetchingMore || isLoading) return;
        fetchPage(pageRef.current + 1, false);
    };

    // On filter change: reset to page 1
    useEffect(() => {
        pageRef.current = 1;
        setHasMore(true);
        fetchPage(1, true);
    }, [startDate, endDate, selectedMode, selectedCategory, selectedWatchlistId, debouncedFlexText, program, batch, selectedInstituteId, refreshKey]);

    // IntersectionObserver — set up once, always calls the latest loadMoreFnRef
    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    loadMoreFnRef.current();
                }
            },
            { threshold: 0.1 }
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, []);

    // Load programs / batches / watchlist names. Super Admin must have an institute first so
    // this never scans the full snapshot (that slow call was racing auto-open on pre-prod).
    useEffect(() => {
        setProgram('');
        setBatch('');

        if (isSuperAdmin) {
            const currentYear = new Date().getFullYear();
            const pastYears = getDomainElementsForGivenYearRange('PAST_YEAR_CODE', 1990, currentYear);
            setBatches(pastYears.map((item: any) => ({ passOutYear: String(item.code) })));
            if (!selectedInstituteId) {
                setPrograms([]);
                setWatchlists([]);
                return;
            }
            const filterBody: Record<string, string> = { instituteId: selectedInstituteId };
            if (campusFilterId) filterBody.filterInstituteId = campusFilterId;
            axiosClient()
                .post(`${process.env.REACT_APP_INTERNAL_ADMIN_SERVER}/search/get-activity-making-filters`, filterBody)
                .then((res) => {
                    if (res.status === 200) {
                        setPrograms(res.data.programs || []);
                        setWatchlists(res.data.watchlists || []);
                    }
                })
                .catch(() => {
                    setPrograms([]);
                    setWatchlists([]);
                });
        } else {
            axiosClient()
                .post(`${process.env.REACT_APP_INTERNAL_ADMIN_SERVER}/search/get-activity-making-filters`, {})
                .then((res) => {
                    if (res.status === 200) {
                        setPrograms(res.data.programs || []);
                        setBatches(res.data.batches || []);
                        setWatchlists(res.data.watchlists || []);
                    }
                })
                .catch(() => { setPrograms([]); setBatches([]); setWatchlists([]); });
        }
    }, [selectedInstituteId, isSuperAdmin, campusFilterId]);

    const loadInstituteOptions = (inputValue: string, callback: (options: any[]) => void) => {
        if (inputValue.length < 1) { callback(instituteOptions.slice(0, 100)); return; }
        callback(instituteOptions.filter((o) => o.label.toLowerCase().includes(inputValue.toLowerCase())));
    };

    const handleInstituteChange = (selected: any) => {
        setSelectedInstituteOption(selected);
        setSelectedInstituteId(selected ? selected.value : '');
        setSelectedWatchlistId('');
    };

    const getInstituteName = (instituteId: any) => {
        const match = instituteDomain.find((item: any) => String(item.code) === String(instituteId));
        return match?.value || String(instituteId || '—');
    };

    const fetchMoengageList = async () => {
        setIsMoengageLoading(true);
        try {
            const payload: any = {};
            if (isSuperAdmin && selectedInstituteId) payload.institute_id = selectedInstituteId;
            else if (!isSuperAdmin) {
                const u = JSON.parse(sessionStorage.getItem('user') || '{}');
                if (u.primaryInstitute) payload.institute_id = u.primaryInstitute;
            }
            const res = await axiosClient().post(
                `${process.env.REACT_APP_BROADCAST_SERVER}/referral/get-moengage-templates`,
                payload
            );
            setMoengageList(res.data?.data || []);
        } catch (err) {
            console.error('Error fetching MoEngage list:', err);
            setMoengageList([]);
        } finally {
            setIsMoengageLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'moengage') fetchMoengageList();
    }, [activeTab, selectedInstituteId]);

    const formatDate = (dateString: any) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    const renderDescription = (activityLog: any) => {
        if (['1', '2'].includes(activityLog?.platformType)) {
            if (!(activityLog?.clicksCount || activityLog?.opensCount)) return `"${activityLog?.subject}"`;
            const prefix = activityLog?.clicksCount ? 'Alumnus clicked Email with subject "' : 'Alumnus opened Email with subject "';
            return `${prefix}${activityLog?.subject}"`;
        }
        if (['3', '4', '5', '6', '8'].includes(activityLog?.platformType)) {
            return <span dangerouslySetInnerHTML={{ __html: activityLog?.subject || '' }} />;
        }
        return activityLog?.subject;
    };

    return (
        <SmartContext.Provider value={{ state, dispatch }}>
            <DebugBarControl />
            <FactLoader show={isLoading && !autoOpenUpload} />
            <div className="main flex-1">
                <section className="alumni_directory_details container-fluid white-block white-block-notopborderradius p-0 h-100">
                    <div className="max-1140 pt-3 pt-sm-5 px-lg-0 pb-5 overflow-hidden mx-auto">
                        <div className="alumni_statistics">
                            <div className="row g-2 mb-3 align-items-center">
                                <div className="col-sm">
                                    <div className="d-flex flex-column">
                                        <h3
                                            data-aos="fade-left"
                                            data-aos-delay="600"
                                            className="font-24 font-600 mb-0 block-title aos-init aos-animate">
                                            Alumni Engagement Activities
                                        </h3>
                                    </div>
                                </div>
                                {allowPerformAction() && (
                                    <div className="col-sm-auto">
                                        <div className="row g-2 align-items-center">
                                            {activeTab === 'activity' && (
                                                <>
                                                    <div className="col-auto">
                                                        <AddNewFormModal
                                                            className="btn btn-green px-3 text-decoration-none"
                                                            isSuperAdmin={isSuperAdmin}
                                                            preselectedInstituteId={selectedInstituteId || null}
                                                            instituteDomain={instituteDomain}
                                                        />
                                                    </div>
                                                    <div className="col-auto">
                                                        <ActivityCsvUploadModal
                                                            className="btn btn-darkblue text-decoration-none"
                                                            onSuccess={() => setRefreshKey((k) => k + 1)}
                                                            autoOpen={autoOpenUpload}
                                                            watchlistId={searchParams.get('watchlist_id')}
                                                            onAutoOpened={clearOpenUploadParam}
                                                        />
                                                    </div>
                                                </>
                                            )}
                                            {activeTab === 'moengage' && (
                                                <div className="col-auto">
                                                    <AddMoengageModal
                                                        className="btn btn-darkblue text-decoration-none"
                                                        isSuperAdmin={isSuperAdmin}
                                                        instituteDomain={instituteDomain}
                                                        onSuccess={fetchMoengageList}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Tab navigation */}
                            <ul className="nav nav-tabs mb-3">
                                <li className="nav-item">
                                    <button
                                        className={`nav-link${activeTab === 'activity' ? ' active' : ''}`}
                                        onClick={() => setActiveTab('activity')}
                                    >
                                        Activity Logs
                                    </button>
                                </li>
                                {isSuperAdmin && (
                                    <li className="nav-item">
                                        <button
                                            className={`nav-link${activeTab === 'moengage' ? ' active' : ''}`}
                                            onClick={() => setActiveTab('moengage')}
                                        >
                                            MoEngage
                                        </button>
                                    </li>
                                )}
                            </ul>

                            <div className="ActiveListings_present">

                                {/* ── Activity Logs tab ── */}
                                {activeTab === 'activity' && (
                                    <>
                                        <div className="d-flex flex-wrap justify-content-between align-items-start align-items-md-center mb-2 filterSearch">
                                            <div className="d-flex flex-wrap flex-1 mb-0 justify-content-start">
                                                <button
                                                    className="btn-grpfiter me-3"
                                                    type="button"
                                                    data-bs-toggle="collapse"
                                                    data-bs-target="#collapseOne"
                                                    aria-expanded="true"
                                                    aria-controls="collapseOne">
                                                    <i className="icon-filter font-13 me-2"></i>
                                                    Filters
                                                </button>
                                                <div className="search alumniSearch m-0 open position-static flex-1">
                                                    <form className="form-inline d-flex flex-wrap justify-content-between d-block">
                                                        <input
                                                            type="search"
                                                            value={flexText}
                                                            onChange={(e) => setFlexText(e.target.value)}
                                                            placeholder="Search by alumni name, email, contact"
                                                            aria-label="Search"
                                                            className="form-control flex-1 font-13 font-500 text-truncate"
                                                        />
                                                        <button
                                                            className="btn btn-search icon icon-search"
                                                            onClick={(e) => e.preventDefault()} />
                                                    </form>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="fitergroup mb-3">
                                            <div id="collapseOne" className="accordion-collapse collapse show" aria-labelledby="headingOne">
                                                <div className="accordion-body p-4">
                                                    <form className="row g-3">
                                                        {isSuperAdmin && (
                                                            <div className="col-md-6">
                                                                <label className="form-label">Institute Name</label>
                                                                <div className="input-group">
                                                                    <div className="input-group-text bg-white rounded-start">
                                                                        <i className="bi bi-search fs-6"></i>
                                                                    </div>
                                                                    <div className="flex-fill form-control border-start-0 p-0">
                                                                        <AsyncSelect
                                                                            cacheOptions
                                                                            loadOptions={loadInstituteOptions}
                                                                            defaultOptions={instituteOptions.slice(0, 100)}
                                                                            onChange={handleInstituteChange}
                                                                            value={selectedInstituteOption}
                                                                            placeholder="Select..."
                                                                            isClearable
                                                                            styles={{ menu: (base) => ({ ...base, zIndex: 1050 }) }}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}

                                                        <div className="col-md-3 col-sm-6">
                                                            <label className="form-label">Program</label>
                                                            <select className="form-select" value={program} onChange={(e) => setProgram(e.target.value)}>
                                                                <option value="">Select</option>
                                                                {programs?.map((p: any) => (
                                                                    <option key={p.programId} value={p.programId}>{p.programName}</option>
                                                                ))}
                                                            </select>
                                                        </div>

                                                        <div className="col-md-3 col-sm-6">
                                                            <label className="form-label">Program Completion Year</label>
                                                            <select className="form-select" value={batch} onChange={(e) => setBatch(e.target.value)}>
                                                                <option value="">Select</option>
                                                                {batches?.map((b: any) => (
                                                                    <option key={b.passOutYear} value={b.passOutYear}>{b.passOutYear}</option>
                                                                ))}
                                                            </select>
                                                        </div>

                                                        <div className="col-md-6">
                                                            <label className="form-label">Activity Date</label>
                                                            <div className="datewrap">
                                                                <div className="from">
                                                                    <input
                                                                        onChange={(e) => setStartDate(e.target.value)}
                                                                        value={startDate}
                                                                        type="date"
                                                                        className="form-control"
                                                                    />
                                                                </div>
                                                                <div className="arrow"><span className="icon-right"></span></div>
                                                                <div className="to">
                                                                    <input
                                                                        onChange={(e) => setEndDate(e.target.value)}
                                                                        value={endDate}
                                                                        type="date"
                                                                        className="form-control"
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="col-md-3 col-sm-6">
                                                            <label className="form-label">Mode</label>
                                                            <select className="form-select" value={selectedMode} onChange={(e) => setSelectedMode(e.target.value)}>
                                                                <option value="">Select</option>
                                                                <option value="CAMPUS_VISIT">Campus Visit</option>
                                                                <option value="EMAIL">Email</option>
                                                                <option value="ONLINE_EVENT">Online Event</option>
                                                                <option value="PHONE_CALL">Phone Call</option>
                                                                <option value="PHYSICAL_EVENT">Physical Event</option>
                                                                <option value="SYSTEM">System</option>
                                                                <option value="WHATSAPP">Whatsapp</option>
                                                                <option value="OTHERS">Others</option>
                                                            </select>
                                                        </div>

                                                        <div className="col-md-3 col-sm-6">
                                                            <label className="form-label">Category</label>
                                                            <select
                                                                className="form-select"
                                                                value={selectedCategory}
                                                                onChange={(e) => {
                                                                    const next = e.target.value;
                                                                    setSelectedCategory(next);
                                                                    if (next !== 'WATCHLIST') setSelectedWatchlistId('');
                                                                }}>
                                                                <option value="">Select</option>
                                                                {ACTIVITY_CATEGORIES.map(({ code, label }) => (
                                                                    <option key={code} value={code}>
                                                                        {label}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                        {selectedCategory === 'WATCHLIST' && (
                                                            <div className="col-md-3 col-sm-6">
                                                                <label className="form-label">Watchlist</label>
                                                                <select
                                                                    className="form-select"
                                                                    value={selectedWatchlistId}
                                                                    onChange={(e) => setSelectedWatchlistId(e.target.value)}>
                                                                    <option value="">All watchlists</option>
                                                                    {watchlists.map((w) => (
                                                                        <option key={w.id} value={String(w.id)}>{w.name}</option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        )}
                                                    </form>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="table-responsive attendee_dtl">
                                            <div className="mb-2 font-14 text-muted">
                                                {isLoading
                                                    ? 'Loading…'
                                                    : `Showing ${activityLogs.length}${activityCount !== undefined ? ` of ${activityCount}` : ''} records`}
                                            </div>

                                            <table className="table" style={{ minWidth: '958px', width: '100%' }}>
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: '150px' }}>Alumni Details</th>
                                                        <th style={{ width: '150px' }}>Activity Date</th>
                                                        <th style={{ width: '200px' }}>Purpose</th>
                                                        <th style={{ width: '450px' }}>Description</th>
                                                        <th style={{ width: '150px' }}>Tracked By</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {isLoading ? (
                                                        <tr>
                                                            <td colSpan={5} className="text-center py-4">
                                                                <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
                                                                Loading records…
                                                            </td>
                                                        </tr>
                                                    ) : activityLogs.length === 0 ? (
                                                        <tr>
                                                            <td colSpan={5} className="text-center py-4 text-muted">No records found.</td>
                                                        </tr>
                                                    ) : (
                                                        activityLogs.map((activityLog: any, idx: number) =>
                                                            activityLog ? (
                                                                <tr key={activityLog.id || idx}>
                                                                    <td>
                                                                        <a
                                                                            className="tablink"
                                                                            href={`/alumni/${activityLog?.uuid}/profile`}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer">
                                                                            <span
                                                                                data-bs-toggle="tooltip"
                                                                                title=""
                                                                                data-bs-original-title={`${activityLog?.firstName} ${activityLog?.lastName}`}
                                                                                className="singlelinename w-100 font-16 lineHeight-16 font-500">
                                                                                {activityLog?.firstName} {activityLog?.lastName}
                                                                                <span className="d-block font-13 fst-italic">
                                                                                    {activityLog?.programName}
                                                                                    {activityLog?.passOutYear ? `, ${activityLog?.passOutYear}` : ''}
                                                                                </span>
                                                                            </span>
                                                                        </a>
                                                                    </td>
                                                                    <td>{formatDate(activityLog?.lastEventTime)}</td>
                                                                    <td>
                                                                        <div className="d-flex flex-column align-items-start lbl_orange font-14">
                                                                            <div><span className="font-500">Category :</span> {getActivityCategoryLabel(activityLog?.category)}</div>
                                                                            <div><span className="font-500">Mode :</span> {activityLog?.mode?.split('_').join(' ')}</div>
                                                                        </div>
                                                                    </td>
                                                                    <td>{renderDescription(activityLog)}</td>
                                                                    <td>
                                                                        <div className="d-flex flex-column align-items-start lbl_orange font-14">
                                                                            <div className="font-500">{activityLog?.addedByName || 'System'}</div>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ) : null
                                                        )
                                                    )}
                                                </tbody>
                                            </table>

                                            {/* Sentinel: IntersectionObserver watches this to trigger next page */}
                                            <div ref={sentinelRef} style={{ height: '1px' }} />

                                            {isFetchingMore && (
                                                <div className="text-center py-3">
                                                    <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
                                                    Loading more…
                                                </div>
                                            )}

                                            {!hasMore && activityLogs.length > 0 && !isLoading && (
                                                <div className="text-center py-3 text-muted font-13">
                                                    All {activityLogs.length} records loaded.
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}

                                {/* ── MoEngage tab — SUPER_ADMIN only ── */}
                                {activeTab === 'moengage' && isSuperAdmin && (
                                    <div className="table-responsive attendee_dtl">
                                        <div className="mb-2 font-14 text-muted">
                                            {isMoengageLoading ? 'Loading…' : `${moengageList.length} record(s)`}
                                        </div>
                                        <table className="table" style={{ minWidth: '900px', width: '100%' }}>
                                            <thead>
                                                <tr>
                                                    {isSuperAdmin && <th style={{ width: '200px' }}>Institute</th>}
                                                    <th>Subject</th>
                                                    <th style={{ width: '120px' }}>Event Date</th>
                                                    <th style={{ width: '100px' }}>Total Sent</th>
                                                    <th style={{ width: '120px' }}>Total Delivered</th>
                                                    <th style={{ width: '80px' }}>Open</th>
                                                    <th style={{ width: '80px' }}>Click</th>
                                                    <th style={{ width: '140px' }}>Created At</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {isMoengageLoading ? (
                                                    <tr>
                                                        <td colSpan={isSuperAdmin ? 8 : 7} className="text-center py-4">
                                                            <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
                                                            Loading…
                                                        </td>
                                                    </tr>
                                                ) : moengageList.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={isSuperAdmin ? 7 : 6} className="text-center py-4 text-muted">
                                                            No records found.
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    moengageList.map((row: any) => (
                                                        <tr key={row.id}>
                                                            {isSuperAdmin && <td>{getInstituteName(row.institute_id)}</td>}
                                                            <td>{row.subject}</td>
                                                            <td>{row.event_date ? formatDate(row.event_date) : '—'}</td>
                                                            <td>{row.sent_count ?? '—'}</td>
                                                            <td>{row.delivered_count ?? '—'}</td>
                                                            <td>{row.open_count ?? '—'}</td>
                                                            <td>{row.click_count ?? '—'}</td>
                                                            <td>{row.created_at ? formatDate(row.created_at) : '—'}</td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </SmartContext.Provider>
    );
};

export default ActivityLogReport;
